import React, { useMemo } from 'react';
import clsx from 'clsx';
import _ from 'lodash';

import { makeStyles } from 'design-system/product/theme';
import Kbd from 'design-system/Kbd';

import { LabelChoice } from '@scale/shared/types/params/annotation';
import AttributeShortcutSelect from './AttributeShortcutSelect';
import useShortcut, { ShortcutMode } from 'react-scale/hooks/useShortcut';
import { Checkbox } from 'design-system';

interface AttributeShortcutInputProps {
  choice: LabelChoice;
  selectedChoice?: string;
  selectedChoices?: string[];
  isReadOnly?: boolean;
  handleSelect: (choice: string) => void;
  index: number;
  isFocused?: boolean;
}

const AttributeShortcutInput: React.FC<AttributeShortcutInputProps> = ({
  choice,
  selectedChoice,
  selectedChoices,
  isReadOnly,
  handleSelect,
  index,
  isFocused,
}) => {
  const classes = useStyles();

  const isMultiSelect = useMemo(() => !!selectedChoices, [selectedChoices]);
  const choiceValue = useMemo(() => (_.isString(choice) ? choice : choice.choice), [choice]);

  const isChoiceSelected = useMemo(() => {
    if (selectedChoice) {
      return selectedChoice === choiceValue;
    } else if (isMultiSelect) {
      return selectedChoices?.includes(choiceValue);
    }
  }, [choiceValue, isMultiSelect, selectedChoice, selectedChoices]);

  // only allow keybinding things
  const keyBindingIndex = useMemo(() => (index <= 9 ? (index + 1) % 10 : null), [index]);

  const choiceDisplay = useMemo(
    () => (_.isString(choice) ? choice : choice.display || choice.choice),
    [choice],
  );

  useShortcut({
    trigger: `${keyBindingIndex ?? 0}`,
    description: 'Select label',
    action: () => {
      !isReadOnly && handleSelect(choiceValue);
    },
    disabled: !isFocused || !keyBindingIndex,
    mode: ShortcutMode.Attributes,
  });

  // Deal with nested choices
  if (!_.isString(choice) && choice.subchoices) {
    return <AttributeShortcutSelect tree={choice} index={index} />;
  }

  return (
    <div
      className={clsx(classes.choice, {
        [classes.selected]: isChoiceSelected,
      })}
      onClick={() => {
        !isReadOnly && handleSelect(choiceValue);
      }}
    >
      <div>
        {keyBindingIndex && <Kbd>{keyBindingIndex}</Kbd>}
        {isMultiSelect && (
          <Checkbox
            label={''}
            checked={isChoiceSelected}
            disabled={isReadOnly}
            classes={{ checkboxRoot: classes.checkboxRoot }}
            edge="end"
            tabIndex={-1}
          />
        )}
      </div>
      <div className="radio-selector__label">{choiceDisplay}</div>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  choice: {
    display: 'flex',
    padding: theme.spacing(0.25),
    margin: theme.spacing(0.25),
    marginLeft: theme.spacing(0.5),
    gap: theme.spacing(1),
  },
  selected: {
    background: theme.colors.periwinkle5,
    border: `2px solid ${theme.colors.periwinkle100}`,
    borderRadius: theme.borderRadius,
  },
  checkboxRoot: {
    paddingLeft: '0px',
    paddingRight: theme.spacing(1),
    paddingTop: '3px',
    paddingBottom: '3px',
  },
}));

export default AttributeShortcutInput;
